// La card de `editar_lanzamiento`: el DIFF y el botón de aplicar —
// ADR-2026-09-09-el-agente-edita-lanzamientos.
//
// Esta card SIEMPRE espera el click, aunque los permisos del chat estén en directo. No es
// una generación: una imagen de más cuesta un crédito y se rehace; un precio mal puesto
// sobre cuarenta productos no tiene ctrl+z, es silencioso, y termina publicado en la
// tienda del cliente. Lo único que evita eso es ver el "de → a" antes de escribir.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowRight, Check, Loader2, PencilLine } from 'lucide-react';
import {
  agruparPorCampo,
  resolverCambios,
  resumenDeEdicionParaAgente,
  type CampoEditable,
  type DiffDeEdicion,
} from './agenteLanzamiento';
import {
  aplicarCambiosDeFicha,
  fetchLanzamiento,
  lanzamientoParaElAgente,
  type ProductoDeLanzamiento,
} from './lanzamientosApi';
import { useAgentEnv } from '../v2/agentEnvContext';
import type { UseV2ChatReturn, V2ToolCard } from '../v2/useV2Chat';

/** Cómo se lee cada campo en la card. El vocabulario del modelo no es el del usuario. */
const ETIQUETA: Record<CampoEditable, string> = {
  nombre: 'Nombre',
  descripcion: 'Descripción',
  precio: 'Precio',
  costo: 'Costo',
  stock: 'Stock',
  sku: 'SKU',
  codigoBarras: 'Código de barras',
  marca: 'Marca',
  tags: 'Etiquetas',
  peso: 'Peso',
  alto: 'Alto',
  ancho: 'Ancho',
  profundidad: 'Profundidad',
  seoTitulo: 'Título SEO',
  seoDescripcion: 'Descripción SEO',
};

/** Filas por campo antes de plegar. Con cuarenta productos, la lista tapa el botón. */
const FILAS_VISIBLES = 6;

type Fase = 'cargando' | 'propuesta' | 'aplicando' | 'listo' | 'error';

export const EditarLanzamientoCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  chat: UseV2ChatReturn;
}> = ({ card, active, chat }) => {
  const { companyId } = useAgentEnv();
  const input = (card.input ?? {}) as { lanzamiento_id?: string; cambios?: unknown; mensaje?: string };
  const [fase, setFase] = useState<Fase>('cargando');
  const [nombreLanzamiento, setNombreLanzamiento] = useState('');
  const [diff, setDiff] = useState<DiffDeEdicion>({ cambios: [], salteados: [] });
  const [error, setError] = useState<string | null>(null);
  const [desplegado, setDesplegado] = useState<Record<string, boolean>>({});
  const arrancoRef = useRef(false);
  const resueltoRef = useRef(false);

  /**
   * Corta el turno con un motivo. La card es la que resuelve la promesa del motor: si
   * termina en error SIN resolver, el chat se queda pensando para siempre
   * (ADR-2026-09-08-las-cards-de-mostrar-tienen-que-montar). Y el agente se entera del
   * porqué, así puede pedir lo que falta en vez de quedarse mudo.
   */
  const fallar = (motivo: string) => {
    setError(motivo);
    setFase('error');
    if (resueltoRef.current) return;
    resueltoRef.current = true;
    chat.resolveToolCard(card.id, { applied: false, motivo });
  };

  // El diff se calcula contra lo que hay AHORA en la base, no contra lo que el modelo cree
  // que hay: entre que leyó el lanzamiento y propuso el cambio, el usuario pudo editar.
  useEffect(() => {
    if (arrancoRef.current || !companyId) return;
    arrancoRef.current = true;
    (async () => {
      try {
        // El `lanzamiento_id` es OPCIONAL en el schema de la tool, así que casi nunca
        // viene: se resuelve igual que en `ver_lanzamiento` —el de la ruta, y si no el
        // borrador más reciente—. Pasarle un id vacío a `fetchLanzamiento` era pedirle
        // una fila que no existe, y la card decía "no encontré ese lanzamiento" con un
        // lanzamiento abierto adelante.
        const lanz = await lanzamientoParaElAgente(companyId, input.lanzamiento_id);
        if (!lanz) {
          fallar(
            input.lanzamiento_id
              ? 'No encontré ese lanzamiento.'
              : 'No hay ningún lanzamiento en borrador para editar.',
          );
          return;
        }
        const detalle = await fetchLanzamiento(lanz.id, companyId);
        if (!detalle) {
          fallar('No pude leer ese lanzamiento.');
          return;
        }
        setNombreLanzamiento(detalle.nombre);
        setDiff(resolverCambios(detalle.productos_ as ProductoDeLanzamiento[], input.cambios));
        setFase('propuesta');
      } catch (err) {
        console.warn('[EditarLanzamiento] cargar', err);
        fallar('No pude leer el lanzamiento.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Sin nada que cambiar, la card no pide un click al pedo: se resuelve sola y el agente
  // se entera de que ya estaba así.
  useEffect(() => {
    if (fase !== 'propuesta' || resueltoRef.current) return;
    if (diff.cambios.length > 0) return;
    resueltoRef.current = true;
    chat.resolveToolCard(card.id, {
      applied: false,
      motivo: 'sin_cambios',
      detalle: resumenDeEdicionParaAgente(diff),
    });
    setFase('listo');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase, diff]);

  const grupos = useMemo(() => agruparPorCampo(diff.cambios), [diff.cambios]);
  const productosTocados = useMemo(
    () => new Set(diff.cambios.map((c) => c.lanzamientoProductoId)).size,
    [diff.cambios],
  );

  const aplicar = async () => {
    if (!active || fase !== 'propuesta' || resueltoRef.current) return;
    resueltoRef.current = true;
    setFase('aplicando');
    try {
      const { productos } = await aplicarCambiosDeFicha(
        diff.cambios.map((c) => ({
          lanzamientoProductoId: c.lanzamientoProductoId,
          campo: c.campo,
          valor: c.a,
        })),
      );
      setFase('listo');
      chat.resolveToolCard(card.id, {
        applied: true,
        cambios: diff.cambios.length,
        productos,
        salteados: diff.salteados,
        detalle: resumenDeEdicionParaAgente(diff),
      });
    } catch (err) {
      console.warn('[EditarLanzamiento] aplicar', err);
      resueltoRef.current = false;
      setError('No pude guardar los cambios.');
      setFase('propuesta');
    }
  };

  const cancelar = () => {
    if (resueltoRef.current) return;
    resueltoRef.current = true;
    setFase('listo');
    chat.resolveToolCard(card.id, { applied: false, motivo: 'cancelado' });
  };

  if (fase === 'cargando') {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-[#02062B]/[0.08] bg-white p-4 text-[13.5px] text-[#02062B]/[0.55]">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo el lanzamiento…
      </div>
    );
  }

  if (fase === 'error') {
    return (
      <div className="rounded-2xl border border-[#02062B]/[0.08] bg-white p-4 text-[13.5px] text-[#C33B36]">
        {error}
      </div>
    );
  }

  if (fase === 'listo') {
    const aplicado = diff.cambios.length > 0;
    return (
      <div className="rounded-2xl border border-[#02062B]/[0.08] bg-white p-4">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
          {aplicado ? 'Listo' : 'Sin cambios'}
        </p>
        <p className="mt-1 flex items-center gap-2 text-[14px] text-brand-black">
          {aplicado && <Check className="h-4 w-4 text-brand-primary" aria-hidden="true" />}
          {aplicado
            ? `Guardé ${diff.cambios.length} cambio(s) en ${productosTocados} producto(s).`
            : 'Los valores ya estaban así.'}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#02062B]/[0.08] bg-white p-3">
      <div className="flex items-center gap-2 px-1 pb-2">
        <PencilLine className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
        <span className="min-w-0">
          <span className="block truncate text-[14px] font-medium text-brand-black">
            {nombreLanzamiento || 'Lanzamiento'}
          </span>
          <span className="block text-[12px] text-[#02062B]/[0.55]">
            {diff.cambios.length} cambio(s) en {productosTocados} producto(s)
          </span>
        </span>
      </div>

      {input.mensaje && (
        <p className="px-1 pb-2 text-[13px] text-[#02062B]/[0.7]">{input.mensaje}</p>
      )}

      <div className="max-h-[300px] space-y-2 overflow-y-auto">
        {grupos.map((g) => {
          const abierto = desplegado[g.campo];
          const visibles = abierto ? g.items : g.items.slice(0, FILAS_VISIBLES);
          return (
            <div key={g.campo} className="rounded-xl border border-[#02062B]/[0.06] p-2.5">
              <p className="pb-1.5 text-[12.5px] font-medium text-brand-black">
                {ETIQUETA[g.campo]}{' '}
                <span className="font-normal text-[#02062B]/[0.5]">
                  · {g.items.length} producto(s)
                </span>
              </p>
              <ul className="space-y-1">
                {visibles.map((c) => (
                  <li
                    key={`${c.lanzamientoProductoId}-${c.campo}`}
                    className="flex items-center gap-2 text-[12.5px]"
                  >
                    <span className="min-w-0 flex-1 truncate text-[#02062B]/[0.7]">
                      {c.nombre || '(sin nombre)'}
                    </span>
                    <span className="shrink-0 text-[#02062B]/[0.45] line-through">
                      {c.de || '—'}
                    </span>
                    <ArrowRight className="h-3 w-3 shrink-0 text-[#02062B]/[0.3]" aria-hidden="true" />
                    <span className="shrink-0 font-medium text-brand-black">{c.a || '—'}</span>
                  </li>
                ))}
              </ul>
              {g.items.length > FILAS_VISIBLES && (
                <button
                  type="button"
                  onClick={() => setDesplegado((d) => ({ ...d, [g.campo]: !abierto }))}
                  className="mt-1 text-[12px] text-brand-primary hover:underline"
                >
                  {abierto ? 'Ver menos' : `Ver los ${g.items.length}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {diff.salteados.length > 0 && (
        <p className="mt-2 flex items-start gap-1.5 px-1 text-[12px] text-[#02062B]/[0.55]">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#C9971C]" aria-hidden="true" />
          <span>
            {diff.salteados.length} producto(s) quedaron sin tocar:{' '}
            {[...new Set(diff.salteados.map((s) => s.motivo))].join('; ')}.
          </span>
        </p>
      )}

      {error && (
        <p role="status" className="mt-2 px-1 text-[12px] text-[#C33B36]">
          {error}
        </p>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={cancelar}
          disabled={!active}
          className="rounded-xl py-2 px-4 text-[13px] font-medium text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.06] disabled:opacity-40"
        >
          No, dejalo así
        </button>
        <button
          type="button"
          onClick={() => void aplicar()}
          disabled={!active || (fase as Fase) === 'aplicando'}
          className="inline-flex items-center gap-1.5 rounded-xl bg-brand-primary py-2 px-4 text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-50"
        >
          {(fase as Fase) === 'aplicando' && (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          )}
          Aplicar los cambios
        </button>
      </div>
    </div>
  );
};

export default EditarLanzamientoCard;
